/**
 * Enum for inspector inspection phase
 * @enum {string}
 * @property {string} AVAILABLE - Inspection is available for the inspector
 * @property {string} NOT_COMPLETED - Inspector started the inspection but did not complete it. The inspector may leave during the inspection (E.g. For a break).
 * @property {string} AWAITING - Inspector has reached out to the client and is waiting for a response.
 * @property {string} RESPONDED - Inspector has received the requested photo or video and can now continue finalizing the inspection.
 * @property {string} CORRECTION - Inspection that have been corrected, meaning they were missing some additional information. I received the data, reviewed the inspection, and completed it. This status helped to filter the inspectors that needed further review.
 * @property {string} REJECTED - Inspection was rejected by the inspector.
 * @property {string} TO_REVIEW - Inspection is ready for review by the supervisor.
 * @property {string} COMPLETED - Inspection confirmed by the supervisor.
 * @property {string} PENDING - Inspection awaiting external processing (e.g. EXPEDITED_AI automated damage detection).
 */
export enum InspectorInspectionPhase {
  AVAILABLE = 'AVAILABLE',
  NOT_COMPLETED = 'NOT_COMPLETED',
  AWAITING = 'AWAITING',
  RESPONDED = 'RESPONDED',
  CORRECTION = 'CORRECTION',
  REJECTED = 'REJECTED',
  TO_REVIEW = 'TO_REVIEW',
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
}

/**
 * Enum for origin step
 * @readonly
 * @enum {string}
 * @property {string} CLIENT - Step detected by client
 * @property {string} INSPECTOR - Step detected by inspector
 */
export enum OriginStep {
  CLIENT = 'CLIENT',
  INSPECTOR = 'INSPECTOR',
}

/**
 * Enum for Tire Tread
 * @readonly
 * @enum {string} TireTreadEnum
 */
export enum TireTreadEnum {
  TREAD_1_32 = '1/32"',
  TREAD_2_32 = '2/32"',
  TREAD_3_32 = '3/32"',
  TREAD_4_32 = '4/32"',
  TREAD_5_32 = '5/32"',
  TREAD_6_32 = '6/32"',
  TREAD_7_32 = '7/32"',
  TREAD_8_32 = '8/32"',
  TREAD_10_32 = '10/32"',
  TREAD_11_32 = '11/32"',
  NA = 'N/A',
}

/**
 * Enum for wheel position
 * @enum {string} WheelPositionEnum
 * @property {string} LEFT_FRONT - 'leftFront'
 * @property {string} RIGHT_FRONT - 'rightFront'
 * @property {string} LEFT_REAR - 'leftRear'
 * @property {string} RIGHT_REAR - 'rightRear'
 */
export enum WheelPositionEnum {
  LEFT_FRONT = 'leftFront',
  RIGHT_FRONT = 'rightFront',
  LEFT_REAR = 'leftRear',
  RIGHT_REAR = 'rightRear',
}

/**
 * Enum for inspector inspection sort fields
 * @enum {string}
 * @property {string} id - Sort by inspection ID
 * @property {string} vin - Sort by vehicle identification number
 * @property {string} status - Sort by inspection status
 * @property {string} createdAt - Sort by creation date
 * @property {string} updatedAt - Sort by last update date
 * @property {string} completedAt - Sort by completion date
 * @property {string} priority - Sort by priority
 */
export enum InspectorInspectionSortField {
  id = 'id',
  vin = 'vin',
  status = 'status',
  createdAt = 'createdAt',
  updatedAt = 'updatedAt',
  completedAt = 'completedAt',
  priority = 'priority',
}

/**
 * Scope used to filter the inspector work queue between regular production work
 * and duplicated training/audit work.
 *
 * @enum {string}
 * @property {string} REGULAR - Only regular (non-training, non-audit) production inspections. Default.
 * @property {string} TRAINING - Only duplicated training inspections.
 * @property {string} AUDIT - Only duplicated audit inspections.
 * @property {string} ALL - Everything (admin only).
 */
export enum InspectorInspectionWorkScope {
  REGULAR = 'REGULAR',
  TRAINING = 'TRAINING',
  AUDIT = 'AUDIT',
  ALL = 'ALL',
}

export enum TireBrandEnum {
  MICHELIN = 'Michelin',
  GOODYEAR = 'Goodyear',
  CONTINENTAL = 'Continental',
  PIRELLI = 'Pirelli',
  BRIDGESTONE = 'Bridgestone',
  COOPER = 'Cooper',
  FALKEN = 'Falken',
  YOKOHAMA = 'Yokohama',
  TOYO = 'Toyo',
  OTHER = 'Other',
  NEXEN = 'Nexen',
  SENTURY = 'Sentury',
  FIRESTONE = 'Firestone',
  CINTURATO = 'Cinturato',
  KENDA = 'Kenda',
  NOT_EQUIPPED = 'Not Equipped',
  EXTREME = 'Extreme',
  BLACKHAWK = 'Blackhawk',
  CROSS_CONTACT = 'Cross Contact',
  BF_GOODRICH = 'BF Goodrich',
  ACHILLES = 'Achilles',
  IRONMAN = 'Ironman',
  HANKOOK = 'Hankook',
  PRINX = 'Prinx',
  ALTIMAX = 'Altimax',
  COOPER_DISCOVERER = 'Cooper Discoverer',
  STARFIRE_SOLARUS = 'Starfire Solarus',
  CORSA = 'Corsa',
  MAXXIS = 'Maxxis',
  HEMISPHERE = 'Hemisphere',
  DUNLOP = 'Dunlop',
  FUZION = 'Fuzion',
  MASTERCRAFT = 'Mastercraft',
  VEE_RUBBER = 'Vee Rubber',
  KELLY = 'Kelly',
  VELOZZA_ZXV4 = 'Velozza ZXV4',
  DEXTERO = 'Dextero',
  NOKIAN = 'Nokian',
  TOYO_TIRES = 'Toyo Tires',
  OTANI = 'Otani',
  WRANGLER = 'Wrangler',
  WESTLAKE_TIRES = 'Westlake Tires',
  KUMHO = 'Kumho',
  DELINTE = 'Delinte',
  SAILUN = 'Sailun',
  NITTO_TIRE = 'Nitto Tire',
  LANDSAIL = 'Landsail',
  MAZAMA_TIRES = 'Mazama Tires',
  FORENZA = 'Forenza',
  NOKIAN_TYRES = 'Nokian Tyres',
  ZENNA_ARGUS = 'Zenna Argus',
  SUMITOMO = 'Sumitomo',
}

/**
 * Enum for sorting fields in the inspector inspection history.
 * @enum {string}
 * @property {string} id - Sort by the unique identifier of the inspection history.
 * @property {string} timestamp - Sort by the timestamp of the inspection history event.
 * @property {string} actor - Sort by the actor who performed the inspection history event.
 */
export enum InspectorInspectionHistorySortField {
  id = 'id',
  timestamp = 'timestamp',
  actor = 'actor',
}

/**
 * Enum for activity actions in inspections.
 * @enum {string}
 */
export enum ActivityAction {
  create = 'create',
  add_ai_data = 'add_ai_data',
  start_inspection = 'start_inspection',
  add_new_step = 'add_new_step',
  add_damage = 'add_damage',
  update_damage = 'update_damage',
  assign_template = 'assign_template',
  clear_template = 'clear_template',
  reassign_inspector = 'reassign_inspector',
  delete_damage = 'delete_damage',
  add_general_note_to_redo_steps = 'add_general_note_to_redo_steps',
  add_note = 'add_note',
  add_note_to_step = 'add_note_to_step',
  send_steps_to_redo = 'send_steps_to_redo',
  cancel_send_steps_to_redo = 'cancel_send_steps_to_redo',
  update_vehicle_info = 'update_vehicle_info',
  update_wheels_info = 'update_wheels_info',
  request_correction = 'request_correction',
  skip_redo_steps = 'skip_redo_steps',
  send_to_review = 'send_to_review',
  mark_as_completed = 'mark_as_completed',
  reject_inspection = 'reject_inspection',
  approve_damage = 'approve_damage',
  revise_redo_steps = 'revise_redo_steps',
  update_status_sliced_photo = 'update_status_sliced_photo',
  update_status_step = 'update_status_step',
  update_status_exterior = 'update_status_exterior',
  update_status_interior = 'update_status_interior',
  update_status_wheel = 'update_status_wheel',
  update_status_mechanical = 'update_status_mechanical',
  generate_estimation_pdf = 'generate_estimation_pdf',
  add_available_panel = 'add_available_panel',
  add_manual_entry = 'add_manual_entry',
  add_miscellaneous_items = 'add_miscellaneous_items',
  commit_estimation = 'commit_estimation',
  generate_estimation = 'generate_estimation',
  set_step_requires_attention = 'set_step_requires_attention',
  mark_for_correction = 'mark_for_correction',
  restore_damage = 'restore_damage',
}

/**
 * Enum for activity visibility levels.
 * @enum {string}
 * @property {string} system - Visibility restricted to the system.
 * @property {string} public - Visibility available to the public.
 */
export enum ActivityVisibility {
  system = 'system',
  public = 'public',
}

/**
 * Enum for sorting fields in the requires attention list.
 * @enum {string}
 * @property {string} id - Sort by the unique identifier.
 * @property {string} createdAt - Sort by creation date.
 * @property {string} updatedAt - Sort by last update date.
 */
export enum RequiresAttentionSortField {
  id = 'id',
  createdAt = 'createdAt',
  updatedAt = 'updatedAt',
}

/**
 * Enum for sources that require attention.
 * @enum {string}
 * @property {string} STEP - Attention required for a step.
 * @property {string} DAMAGE - Attention required for damage.
 */
export enum SourceRequiresAttention {
  STEP = 'STEP',
  DAMAGE = 'DAMAGE',
}

/**
 * Enum for rotation targets in degrees.
 * @enum {number}
 * @property {number} deg0 - 0 degrees rotation.
 * @property {number} deg90 - 90 degrees rotation.
 * @property {number} deg180 - 180 degrees rotation.
 * @property {number} deg270 - 270 degrees rotation.
 */
export enum RotationTarget {
  deg0 = 0,
  deg90 = 90,
  deg180 = 180,
  deg270 = 270,
}

/**
 * Enum of supported top-level sections for inspector inspection response shaping.
 *
 * Used in flexible selection to control which blocks are included
 * in the `IInspectorInspectionView` payload.
 *
 * @enum {string}
 * @property {string} General - Includes the `general` section.
 * @property {string} Responsibility - Includes the `responsibility` section.
 * @property {string} Notes - Includes the `notes` section.
 * @property {string} Vehicle - Includes the `vehicle` section.
 * @property {string} VinData - Includes the `vinData` section.
 * @property {string} ExtendedInfo - Includes the `extended_info` section with additional details.
 * @property {string} AuditTraining - Includes the `auditTraining` section with training-related information.
 * @property {string} EstReq - Includes the `estimateRequest` section with estimation request details.
 */
export enum InspectorInspectionInclude {
  General = 'general',
  Responsibility = 'responsibility',
  Notes = 'notes',
  Vehicle = 'vehicle',
  VinData = 'vinData',
  ExtendedInfo = 'extended_info',
  AuditTraining = 'auditTraining',
  EstReq = 'estimateRequest',
  Flow = 'flow',
}

/**
 * Convenience list of all supported `include` values for inspector inspections.
 *
 * Useful for validation, parsing, and request normalization.
 *
 * @type {InspectorInspectionInclude[]}
 */
export const INSPECTOR_INSPECTION_INCLUDES = Object.values(InspectorInspectionInclude);

/**
 * Enum of supported nested fields for inspector inspection response expansion.
 *
 * Used to resolve specific nested properties within already included top-level
 * sections of the inspector inspection response.
 *
 * @enum {string}
 * @property {string} GeneralInstructions \- Expands `general.instructions`.
 * @property {string} GeneralTimestamps \- Expands `general.timestamps`.
 * @property {string} GeneralCompany \- Expands `general.company`.
 * @property {string} GeneralRate \- Expands `general.rate`.
 * @property {string} GeneralPriority \- Expands `general.priority`.
 * @property {string} GeneralTemplateId \- Expands `general.templateId`.
 * @property {string} VehicleCarBodyType \- Expands `vehicle.carBodyType`.
 * @property {string} VehicleVehicleInfo \- Expands `vehicle.vehicleInfo`.
 * @property {string} VehicleExternalResourceUrl \- Expands `vehicle.externalResourceUrl`.
 */
export enum InspectorInspectionExpand {
  GeneralInstructions = 'general.instructions',
  GeneralTimestamps = 'general.timestamps',
  GeneralCompany = 'general.company',
  GeneralRate = 'general.rate',
  GeneralPriority = 'general.priority',
  GeneralTemplateId = 'general.templateId',
  VehicleCarBodyType = 'vehicle.carBodyType',
  VehicleVehicleInfo = 'vehicle.vehicleInfo',
  VehicleExternalResourceUrl = 'vehicle.externalResourceUrl',
}

/**
 * Convenience list of all supported `expand` values for inspector inspections.
 *
 * Useful for validation, parsing, and request normalization.
 *
 * @type {InspectorInspectionExpand[]}
 */
export const INSPECTOR_INSPECTION_EXPANDS = Object.values(InspectorInspectionExpand);

/**
 * Enum for inspection tabs.
 * @enum {string}
 * @property {string} VIEW_360 - 360-degree inspection view.
 * @property {string} EXTERIOR - Exterior inspection section.
 * @property {string} INTERIOR - Interior inspection section.
 * @property {string} WHEELS - Wheels inspection section.
 * @property {string} MECHANICAL - Mechanical inspection section.
 */
export enum InspectionTab {
  VIEW_360 = 'video',
  EXTERIOR = 'exterior',
  INTERIOR = 'interior',
  WHEELS = 'wheel',
  MECHANICAL = 'mechanical',
}
